import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import DashboardLayout from '../../components/DashboardLayout'
import { portfolioService, usersService } from '../../services/api'
import {
  mapPortfolioToArtisanForm,
  mapArtisanFormToPortfolio,
  validateArtisanForm,
} from '../../utils/artisanProfileMapper'
import { getApiErrorMessage, unwrapApiData } from '../../utils/proPageMapper'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Button } from '../../components/ui/button'
import { Textarea } from '../../components/ui/textarea'
import { Badge } from '../../components/ui/badge'
import { Checkbox } from '../../components/ui/checkbox'
import { toast } from 'sonner'
import { Building2, Award, Plus, X } from 'lucide-react'

const categories = [
  'Plomberie', 'Électricité', 'Peinture', 'Maçonnerie', 'Menuiserie',
  'Rénovation', 'Toiture', 'Serrurerie', 'Carrelage', 'Climatisation',
  'Chauffage', 'Isolation', 'Jardinage', 'Nettoyage', 'Déménagement',
]

const EMPTY_FORM = {
  nom_commercial: '',
  description: '',
  categories: [],
  localisation: '',
  zone_intervention: [],
  annees_experience: '',
  certifications: [],
  horaires: '',
  telephone_pro: '',
  email_pro: '',
  site_web: '',
  disponibilite: 'Disponible',
}

export default function MyArtisanProfile() {
  const { user, hasModule } = useAuth()
  const [profileMeta, setProfileMeta] = useState(null)
  const [existingPortfolio, setExistingPortfolio] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [newZone, setNewZone] = useState('')
  const [newCertification, setNewCertification] = useState('')

  useEffect(() => {
    fetchProfile()
  }, [])

  const applyPortfolioPayload = (payload) => {
    const mapped = mapPortfolioToArtisanForm(payload, user)
    const portfolioData = payload?.portfolioData ?? payload ?? {}

    setExistingPortfolio(portfolioData)
    setProfileMeta(mapped.meta)
    setFormData({
      nom_commercial: mapped.nom_commercial,
      description: mapped.description,
      categories: mapped.categories,
      localisation: mapped.localisation,
      zone_intervention: mapped.zone_intervention,
      annees_experience: mapped.annees_experience,
      certifications: mapped.certifications,
      horaires: mapped.horaires,
      telephone_pro: mapped.telephone_pro,
      email_pro: mapped.email_pro,
      site_web: mapped.site_web,
      disponibilite: mapped.disponibilite,
    })

    return mapped
  }

  const fetchProfile = async () => {
    try {
      const response = await portfolioService.getPortfolio()
      applyPortfolioPayload(unwrapApiData(response))
    } catch (error) {
      console.error('Error fetching artisan profile:', error)
      toast.error(getApiErrorMessage(error, 'Impossible de charger votre fiche artisan'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validateArtisanForm(formData)
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join(' · '))
      return
    }

    setSaving(true)

    try {
      const portfolioData = mapArtisanFormToPortfolio(formData, existingPortfolio ?? {})
      const response = await portfolioService.updatePortfolio(portfolioData)
      applyPortfolioPayload(unwrapApiData(response))

      if (formData.localisation?.trim()) {
        await usersService.updateMyProfile({ ville: formData.localisation.trim() })
      }

      toast.success('Profil mis à jour')
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la mise à jour'))
    } finally {
      setSaving(false)
    }
  }

  const toggleCategory = (cat) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter((c) => c !== cat)
        : [...prev.categories, cat],
    }))
  }

  const addZone = () => {
    const zone = newZone.trim()
    if (!zone || formData.zone_intervention.includes(zone)) return

    setFormData((prev) => ({
      ...prev,
      zone_intervention: [...prev.zone_intervention, zone],
    }))
    setNewZone('')
  }

  const removeZone = (zone) => {
    setFormData((prev) => ({
      ...prev,
      zone_intervention: prev.zone_intervention.filter((z) => z !== zone),
    }))
  }

  const addCertification = () => {
    const cert = newCertification.trim()
    if (!cert || formData.certifications.includes(cert)) return

    setFormData((prev) => ({
      ...prev,
      certifications: [...prev.certifications, cert],
    }))
    setNewCertification('')
  }

  const removeCertification = (cert) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((c) => c !== cert),
    }))
  }

  if (!hasModule('artisan_profile_access')) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Accès restreint</h2>
          <p className="text-slate-600 mb-4">
            Vous devez avoir l&apos;accès au module &quot;Fiche artisan&quot; pour créer votre profil professionnel.
          </p>
          <Button onClick={() => { window.location.href = '/tableau-de-bord/demandes-acces' }}>
            Demander l&apos;accès
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Ma fiche artisan</h1>
            <p className="text-slate-600">Gérez votre profil professionnel</p>
          </div>
          {profileMeta && (
            <div className="flex items-center gap-2">
              {profileMeta.is_verified && <Badge className="badge-verified">Vérifié</Badge>}
              {profileMeta.is_premium && <Badge className="badge-premium">Premium</Badge>}
              {profileMeta.is_visible && <Badge className="badge-new">Visible</Badge>}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="artisan-form">
          <div className="lux-dashboard-card p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Informations générales</h2>

            <div className="space-y-4">
              <div>
                <Label className="form-label">Nom commercial *</Label>
                <Input
                  required
                  value={formData.nom_commercial}
                  onChange={(e) => setFormData({ ...formData, nom_commercial: e.target.value })}
                  placeholder="Ex: Plomberie Dupont"
                  data-testid="artisan-nom"
                />
              </div>

              <div>
                <Label className="form-label">Description *</Label>
                <Textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez votre activité, vos spécialités..."
                  data-testid="artisan-description"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Années d&apos;expérience *</Label>
                  <Input
                    type="number"
                    min="0"
                    required
                    value={formData.annees_experience}
                    onChange={(e) => setFormData({ ...formData, annees_experience: e.target.value })}
                    placeholder="10"
                    data-testid="artisan-experience"
                  />
                </div>
                <div>
                  <Label className="form-label">Disponibilité</Label>
                  <Input
                    value={formData.disponibilite}
                    onChange={(e) => setFormData({ ...formData, disponibilite: e.target.value })}
                    placeholder="Disponible, Occupé, Sous 48h..."
                    data-testid="artisan-disponibilite"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lux-dashboard-card p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Catégories métiers *</h2>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <label
                  key={cat}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    formData.categories.includes(cat)
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.categories.includes(cat)}
                    onCheckedChange={() => toggleCategory(cat)}
                  />
                  {cat}
                </label>
              ))}
            </div>
          </div>

          <div className="lux-dashboard-card p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Localisation</h2>

            <div className="space-y-4">
              <div>
                <Label className="form-label">Ville principale *</Label>
                <Input
                  required
                  value={formData.localisation}
                  onChange={(e) => setFormData({ ...formData, localisation: e.target.value })}
                  placeholder="Paris"
                  data-testid="artisan-localisation"
                />
              </div>

              <div>
                <Label className="form-label">Zones d&apos;intervention</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newZone}
                    onChange={(e) => setNewZone(e.target.value)}
                    placeholder="Ajouter une zone"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addZone()
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={addZone}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.zone_intervention.map((zone) => (
                    <Badge key={zone} variant="secondary" className="flex items-center gap-1">
                      {zone}
                      <button
                        type="button"
                        aria-label={`Retirer ${zone}`}
                        className="inline-flex"
                        onClick={() => removeZone(zone)}
                      >
                        <X className="w-3 h-3 cursor-pointer" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lux-dashboard-card p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Contact professionnel</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Téléphone pro *</Label>
                <Input
                  required
                  value={formData.telephone_pro}
                  onChange={(e) => setFormData({ ...formData, telephone_pro: e.target.value })}
                  placeholder="06 12 34 56 78"
                  data-testid="artisan-telephone"
                />
              </div>
              <div>
                <Label className="form-label">Email pro *</Label>
                <Input
                  type="email"
                  required
                  value={formData.email_pro}
                  onChange={(e) => setFormData({ ...formData, email_pro: e.target.value })}
                  placeholder="contact@entreprise.fr"
                  data-testid="artisan-email"
                />
              </div>
              <div>
                <Label className="form-label">Site web</Label>
                <Input
                  value={formData.site_web}
                  onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                  placeholder="https://www.monsite.fr"
                />
              </div>
              <div>
                <Label className="form-label">Horaires</Label>
                <Input
                  value={formData.horaires}
                  onChange={(e) => setFormData({ ...formData, horaires: e.target.value })}
                  placeholder="Lun-Ven: 8h-18h"
                />
              </div>
            </div>
          </div>

          <div className="lux-dashboard-card p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Certifications</h2>

            <div className="flex gap-2 mb-2">
              <Input
                value={newCertification}
                onChange={(e) => setNewCertification(e.target.value)}
                placeholder="Ex: RGE, Qualibat..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCertification()
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addCertification}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.certifications.map((cert) => (
                <Badge key={cert} variant="outline" className="flex items-center gap-1">
                  <Award className="w-3 h-3" />
                  {cert}
                  <button
                    type="button"
                    aria-label={`Retirer ${cert}`}
                    className="inline-flex"
                    onClick={() => removeCertification(cert)}
                  >
                    <X className="w-3 h-3 cursor-pointer" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={saving} data-testid="artisan-submit">
              {saving ? 'Enregistrement...' : existingPortfolio?.headline ? 'Mettre à jour' : 'Créer le profil'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}
